/**
 * Crew rotation contract (Feature 2): the shared, IMPORT-FREE half.
 *
 * A crew rotation is a periodic calendar saying which crew works the day slot
 * and which works the night slot on each date. Unilever Jefferson City runs
 * crews A-D on 12-hour shifts (A and C on days 06-18, B and D on nights 18-06,
 * America/Chicago, alternating on a fixed calendar). A/C and B/D share their
 * hours, so only the date can tell them apart.
 *
 * ZERO IMPORTS, pinned by a source scan in
 * apps/api/src/lib/__tests__/shift-rotation-contract.test.ts, so that apps/web
 * can deep-import this file as `@guild/shared/shift-rotation.js` (the
 * `@guild/config/*.config.js` precedent) without pulling zod into the web.
 * The Zod schema, `shiftRotationSchema`, lives in `schemas/shift.ts` and is
 * built on `rotationProblems`.
 *
 * SEMANTICS
 *   Cycle day 0 is `anchorDate`. On that date `dayCrews[0]` works
 *   [dayStartHour, nightStartHour), and `nightCrews[0]` works the night that
 *   STARTS that evening at nightStartHour and runs into the next date until
 *   dayStartHour. Entry i of each list is cycle day i, and the cycle length is
 *   the list length.
 *
 * DAY NUMBERS
 *   A day number names a calendar date, not an instant: whole days since
 *   1970-01-01 in the proleptic Gregorian calendar (1970-01-01 = 0,
 *   1969-12-31 = -1). An instant becomes a day number only through
 *   `localDayNumber`, in a named time zone.
 *
 * NOTHING HERE THROWS (backend review F1). Every export returns a value for
 * any plain-data input (a JSON document, a request body, a half-filled form,
 * NaN, a wrong type) and reports what is wrong instead. Zod's `safeParse`
 * does not catch an exception thrown inside `.superRefine`, so a RangeError
 * from a `Date` round trip on `anchorDate: ''` would escape it and reach every
 * caller that validates untrusted input. The failure values are:
 * `rotationProblems` -> a message; a day number -> NaN; a date string -> '';
 * `rotationCrewsOnDay` -> null.
 */

/** An org's crew rotation. */
export interface ShiftRotation {
  /** Cycle day 0, as `YYYY-MM-DD`. Required, with no default (EM review F7). */
  anchorDate: string;
  /** Local hour (0-23) the day slot starts and the night slot ends. */
  dayStartHour: number;
  /** Local hour (0-23) the night slot starts and the day slot ends. */
  nightStartHour: number;
  /** The day-slot crew code for each cycle day. */
  dayCrews: string[];
  /** The night-slot crew code for each cycle day: the night that starts on that date. */
  nightCrews: string[];
}

/** The crews a rotation puts on one calendar date. */
export interface RotationCrews {
  /** 0-based position of the date in the cycle (0 = the anchor date). */
  cycleDay: number;
  /** Works the day slot on that date. */
  dayCrew: string;
  /** Works the night slot that starts on that date. */
  nightCrew: string;
}

/** Longest cycle a rotation may declare, in days (8 weeks). */
export const ROTATION_MAX_CYCLE_DAYS = 56;

const MS_PER_DAY = 86_400_000;

/** Exactly `YYYY-MM-DD`: a four-digit year, a two-digit month and day. */
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A crew code as `shifts.code` holds it (the shifts routes lower-case codes on write). */
const CREW_CODE = /^[a-z0-9_-]{1,20}$/;

/** Plausible anchor years. Anything outside is a typo, such as a two-digit year read as 0026. */
const ANCHOR_YEAR_MIN = 2000;
const ANCHOR_YEAR_MAX = 2099;

/**
 * The five fields of a `ShiftRotation`. Typed against the interface, so a field
 * added there fails type-check until it is added here as well.
 */
const ROTATION_FIELDS: ReadonlySet<string> = new Set(
  Object.keys({
    anchorDate: true,
    dayStartHour: true,
    nightStartHour: true,
    dayCrews: true,
    nightCrews: true,
  } satisfies Record<keyof ShiftRotation, true>)
);

/** Longest field name a message shows; a longer one is cut, with an ellipsis. */
const MAX_SHOWN_FIELD = 40;

/**
 * Day number of a real calendar date written `YYYY-MM-DD`, or NaN for anything
 * else: a wrong shape, a non-string, or a date that does not exist
 * (`2026-02-30`, `2027-02-29`). The round trip through `dayNumberToYmd` is what
 * rejects a date `Date` would silently roll over into the next month.
 *
 * `setUTCFullYear`, not `Date.UTC`: `Date.UTC` reads years 0-99 as 1900-1999,
 * which would break the round trip for 0000-0099. With it, this function and
 * `dayNumberToYmd` are exact inverses over 0000-01-01 to 9999-12-31.
 */
export function ymdToDayNumber(ymd: string): number {
  const match = typeof ymd === 'string' ? YMD.exec(ymd) : null;
  if (match === null) return NaN;
  const date = new Date(0);
  date.setUTCFullYear(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const day = date.getTime() / MS_PER_DAY;
  return dayNumberToYmd(day) === ymd ? day : NaN;
}

/**
 * `YYYY-MM-DD` for a day number, or '' when `day` is not an integer or names a
 * date outside 0000-9999 (where `toISOString` would throw, or write a
 * six-digit year).
 */
export function dayNumberToYmd(day: number): string {
  if (!Number.isInteger(day)) return '';
  const date = new Date(day * MS_PER_DAY);
  if (Number.isNaN(date.getTime())) return '';
  const ymd = date.toISOString().slice(0, 10);
  return YMD.test(ymd) ? ymd : '';
}

/**
 * Day number of the calendar date at instant `at` in `timeZone`: the org's
 * LOCAL date, never the UTC date. The two differ every evening. At 19:00 CDT
 * the UTC date is already tomorrow, so a UTC "today" shifts a rotation entered
 * in the evening by one day, which on an alternating pattern swaps A with C and
 * B with D (backend review F6, EM review F7).
 *
 * Reads only year, month and day from `formatToParts`, so no hour field, and
 * so no h24 midnight render, can reach the result. `hourCycle: 'h23'` is inert
 * while no hour is requested (V8 11.3 and 12.4 both resolve none); it is set so
 * that adding an hour later cannot bring back the h24 render. Never
 * `hour12: false`: see `getLocalHour` in apps/api/src/lib/timezone.ts.
 *
 * NaN when `at` is not a valid Date or `timeZone` is not a zone Intl knows. A
 * missing `timeZone` is NaN too, never the host's zone: Intl falls back to the
 * host zone when `timeZone` is undefined, which is the wrong answer this
 * function exists to prevent.
 */
export function localDayNumber(at: Date, timeZone: string): number {
  if (!(at instanceof Date) || typeof timeZone !== 'string') return NaN;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
  } catch {
    // RangeError: an invalid Date, or a zone name Intl does not know.
    // TypeError: an object that passes `instanceof Date` without being one.
    return NaN;
  }
  const field = (type: 'year' | 'month' | 'day'): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return ymdToDayNumber(`${field('year')}-${field('month')}-${field('day')}`);
}

/**
 * The crews on calendar day `day` (a day number). The cycle day is
 * `(((day - anchor) % n) + n) % n`, so dates before the anchor resolve too:
 * JS `%` keeps the sign of `day - anchor`, and the `+ n` folds it back.
 *
 * null, never an exception, when the rotation cannot be evaluated (the anchor
 * is not a real date; a list is missing, empty, or a different length from the
 * other; the entry for that day is not text) or `day` is not an integer. What
 * to do then is the caller's decision. It does not apply the other
 * `rotationProblems` rules (hours, code format, cycle length, crews on both
 * slots), so check a rotation with `rotationProblems` (or safe-parse it) before
 * trusting its crews.
 */
export function rotationCrewsOnDay(rotation: ShiftRotation, day: number): RotationCrews | null {
  const r: unknown = rotation;
  if (!isRecord(r) || !Array.isArray(r.dayCrews) || !Array.isArray(r.nightCrews)) return null;
  const n = r.dayCrews.length;
  if (n === 0 || r.nightCrews.length !== n || !Number.isInteger(day)) return null;
  const anchor = ymdToDayNumber(r.anchorDate as string);
  if (Number.isNaN(anchor)) return null;
  const cycleDay = (((day - anchor) % n) + n) % n;
  const dayCrew: unknown = r.dayCrews[cycleDay];
  const nightCrew: unknown = r.nightCrews[cycleDay];
  if (typeof dayCrew !== 'string' || typeof nightCrew !== 'string') return null;
  return { cycleDay, dayCrew, nightCrew };
}

/**
 * Every reason `rotation` cannot be used, as plain-English messages; [] means
 * valid. With `activeShiftCodes` (the org's active `shifts.code` values) it
 * also checks each crew code against them, case-insensitively.
 *
 * First, every own key other than the five `ShiftRotation` fields gets
 * `Unknown field "<key>"` (a key over 40 characters is cut, with an ellipsis).
 * `shiftRotationSchema` is `.strict()` and rejects such a value outright, so
 * without this check a value that passed here could still fail the schema.
 *
 * Then the rules, in the order their messages appear:
 *   1. `anchorDate` is a real `YYYY-MM-DD` date from 2000 to 2099. It is
 *      required and has no default (EM review F7), so '' fails the shape check.
 *      The shape and the year are checked BEFORE the `Date` round trip, so
 *      nothing malformed reaches `Date`.
 *   2. Each start hour is a whole hour from 0 to 23.
 *   3. The day slot starts before the night slot.
 *   4. Each list has 1 to ROTATION_MAX_CYCLE_DAYS entries, and both lists have
 *      the same length.
 *   5. Each crew code is 1-20 lower-case letters, digits, '-' or '_'.
 *   6. No cycle day puts one crew on both slots.
 *   7. With `activeShiftCodes`: each code matches exactly one active shift.
 *
 * Typed `unknown` because it is total (see the file header): a wrong-typed
 * field gets that field's message, so a draft can be checked as it is typed,
 * and a stored value can be checked without a cast.
 */
export function rotationProblems(
  rotation: unknown,
  activeShiftCodes?: readonly string[]
): string[] {
  if (!isRecord(rotation)) return ['The crew rotation must be an object'];
  const { anchorDate, dayStartHour, nightStartHour, dayCrews, nightCrews } = rotation;
  const problems: string[] = [];

  // Unknown fields: shiftRotationSchema is .strict(), so a sixth key fails it.
  for (const key of Object.keys(rotation)) {
    if (!ROTATION_FIELDS.has(key)) problems.push(`Unknown field "${shownField(key)}"`);
  }

  // 1. Anchor date.
  const anchorProblem = anchorDateProblem(anchorDate);
  if (anchorProblem !== null) problems.push(anchorProblem);

  // 2-3. Slot hours.
  const dayStartOk = isHour(dayStartHour);
  const nightStartOk = isHour(nightStartHour);
  if (!dayStartOk) problems.push('Day shift start must be a whole hour from 0 to 23');
  if (!nightStartOk) problems.push('Night shift start must be a whole hour from 0 to 23');
  if (dayStartOk && nightStartOk && dayStartHour >= nightStartHour) {
    problems.push(
      `Day shift start (${clock(dayStartHour)}) must be earlier than night shift start (${clock(nightStartHour)})`
    );
  }

  // 4. List lengths.
  const days = Array.isArray(dayCrews) ? (dayCrews as unknown[]) : null;
  const nights = Array.isArray(nightCrews) ? (nightCrews as unknown[]) : null;
  const daysOk = days !== null && days.length >= 1 && days.length <= ROTATION_MAX_CYCLE_DAYS;
  const nightsOk =
    nights !== null && nights.length >= 1 && nights.length <= ROTATION_MAX_CYCLE_DAYS;
  const listRule = `1 to ${ROTATION_MAX_CYCLE_DAYS} crews, one per day of the cycle`;
  if (!daysOk) problems.push(`Day crews must list ${listRule}`);
  if (!nightsOk) problems.push(`Night crews must list ${listRule}`);
  if (daysOk && nightsOk && days.length !== nights.length) {
    problems.push(
      `Day crews and night crews must list the same number of days (${days.length} and ${nights.length})`
    );
  }

  // 5. Code format, once per distinct entry.
  const entries = new Set<unknown>([...(days ?? []), ...(nights ?? [])]);
  const codes: string[] = [];
  for (const entry of entries) {
    if (typeof entry === 'string' && CREW_CODE.test(entry)) {
      codes.push(entry);
    } else {
      problems.push(
        `Crew code "${asText(entry)}" must be 1 to 20 lower-case letters, digits, "-" or "_"`
      );
    }
  }

  // 6. One crew on both slots of a cycle day.
  if (days !== null && nights !== null) {
    for (let i = 0; i < Math.min(days.length, nights.length); i++) {
      const dayCrew = days[i];
      const nightCrew = nights[i];
      if (
        typeof dayCrew === 'string' &&
        typeof nightCrew === 'string' &&
        dayCrew.toLowerCase() === nightCrew.toLowerCase()
      ) {
        problems.push(
          `Crew "${dayCrew}" cannot work both the day and the night shift on day ${i + 1} of the cycle`
        );
      }
    }
  }

  // 7. Each well-formed code names exactly one active shift. Anything but
  // `undefined` runs the rule; a value that is not a list counts as no shifts.
  if (activeShiftCodes !== undefined) {
    const active = (Array.isArray(activeShiftCodes) ? (activeShiftCodes as unknown[]) : [])
      .filter((code): code is string => typeof code === 'string')
      .map((code) => code.toLowerCase());
    for (const code of codes) {
      const matches = active.filter((activeCode) => activeCode === code).length;
      if (matches === 0) {
        problems.push(`Crew code "${code}" does not match any active shift`);
      } else if (matches > 1) {
        problems.push(`Crew code "${code}" matches more than one active shift`);
      }
    }
  }

  return problems;
}

/**
 * Rule 1: the shape and the year first, then the round trip, so `Date` only
 * ever sees a well-formed date. A missing or empty anchor fails the shape
 * check: it is required, with no default.
 */
function anchorDateProblem(anchorDate: unknown): string | null {
  if (typeof anchorDate !== 'string' || !YMD.test(anchorDate)) {
    return 'Start date must be YYYY-MM-DD';
  }
  const year = Number(anchorDate.slice(0, 4));
  if (!(year >= ANCHOR_YEAR_MIN && year <= ANCHOR_YEAR_MAX)) {
    return `Start date must be between ${ANCHOR_YEAR_MIN}-01-01 and ${ANCHOR_YEAR_MAX}-12-31`;
  }
  if (Number.isNaN(ymdToDayNumber(anchorDate))) {
    return `Start date ${anchorDate} is not a real calendar date`;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isHour(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 23;
}

/** 6 -> "06:00". */
function clock(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * A list entry as message text. Objects are never converted: `String()` throws
 * on a plain JSON object whose `toString` is not a function, such as
 * `{"toString": null}`.
 */
function asText(value: unknown): string {
  return typeof value === 'object' && value !== null ? '[object]' : String(value);
}

/** A field name as message text, cut so a 100k-character key cannot bloat the message. */
function shownField(key: string): string {
  return key.length > MAX_SHOWN_FIELD ? `${key.slice(0, MAX_SHOWN_FIELD)}…` : key;
}
